import { Box, Button, Stack, Typography, Grid } from '@vapor/material'
import { useConfirm } from 'hic-components'
import { useEffect, useState, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { BillListItemFragment, ChargeCategoryFragment, useBillsListQuery, usePosDepositDocumentsQuery, useProductsQuery, PosDepositDocumentFragment } from 'src/api/api.generated'
import { useHotelDate, useTenant } from 'src/app/hooks/context/context.hook'
import { compareDates, dateEquality } from 'src/utils/date'
import usePosCRUD from './pos-crud.hook'
import { isFragment, isId } from 'src/app/components/query-lookup/query-lookup'
import { HicDataGridPremium, useHicCommonDataGridProps } from 'src/app/components/hic-vapor/datagrid/datagrid'
import usePosPasserbyBillsList, { getCheckinFromBill, getCheckoutFromBill } from '../bill/components/bills-list/pos-passerby-bills-list.hook'
import { PosPasserbyBillsListRow } from '../bill/components/bills-list/pos-passerby-bills-types'
import PosActionsRow from './components/pos-actions-row'
import PosChargeCategorySelector from './components/pos-charge-category-selector'
import PosNewProduct from './components/pos-new-product'
import PosProducts from './components/pos-products'
import CheckoutDocumentForm from 'src/app/pages/accounting/documents/checkout-document/checkout-document-form'
import useInfoSnackBar from 'src/app/hooks/info-snack-bar/use-info-snack-bar'
import useErrorSnackBar from 'src/app/hooks/error-snack-bar/error-snack-bar'
import AccountBalanceReportViewer from 'src/app/components/report-viewer/account-balance/account-balance-report-viewer'
import PasserbyBillListDrawer from '../bill/passerby-bill.list.drawer'
import { HtmlElementId } from 'src/app/layout/html-element-id.type'
import DocumentReportViewer from 'src/app/components/report-viewer/documnent/document-report-viewer'
import { v4 as uuid } from 'uuid'
import FormLayout from 'src/app/layout/form-layout/form-layout'
import useFormatCurrency from 'src/app/hooks/current-locale/use-format-currency'
import { PosDepositInfo } from './components/pos-deposit-info'

type PosTypeProps = {
  propertyId: string
  billId?: string
  onSave?: (success: boolean) => void
  onCancel?: () => void
  asPage: boolean
}

export default function Pos(props: PosTypeProps) {
  const { propertyId, asPage, billId, onCancel } = props
  const { hicCommonDataGridPremiumProps } = useHicCommonDataGridProps()

  const showError = useErrorSnackBar()
  const infoSnackBar = useInfoSnackBar()
  const hotelDate = useHotelDate()
  const { t } = useTranslation()
  const { formatCurrency } = useFormatCurrency()
  const confirm = useConfirm()
  const { id: tenantId, currency } = useTenant()
  const [selectedChargeCategory, setSelectedChargeCategory] = useState<ChargeCategoryFragment | undefined>()
  const [loading, setLoading] = useState(false)
  const [printDocumentId, setPrintDocumentId] = useState<string | null>(null)
  const [bill, setBill] = useState<BillListItemFragment | { id: string } | null>(props.billId ? { id: props.billId } : null)
  const [posDeposits, setPosDeposits] = useState<PosDepositDocumentFragment[]>([])

  const billRelatedToReservation = useMemo(() => {
    return Boolean(props.billId)
  }, [props.billId])

  const {
    editBill,
    createDummyBill,
    loading: loadingCRUD,
  } = usePosCRUD(propertyId, () => {
    props.onSave ? props.onSave(false) : undefined
  })

  //#region bill grid
  const {
    data: billsData,
    loading: loadingBills,
    refetch,
  } = useBillsListQuery({
    notifyOnNetworkStatusChange: true,
    skip: true,
  })

  const fetchBill = useCallback(
    async (billId: string) => {
      try {
        const result = await refetch({
          filter: {
            tenantId: {
              eq: tenantId,
            },
            propertyId: {
              eq: propertyId,
            },
            reservationId: billId
              ? undefined
              : {
                  exists: false,
                },
            reservedResourceId: billId
              ? undefined
              : {
                  exists: false,
                },
            id: {
              eq: billId,
            },
          },
        })
        if (!result.error && result.data) {
          return result.data?.bills?.find((b) => b)
        } else {
          return undefined
        }
      } catch (error) {
        return undefined
      }
    },
    [tenantId, propertyId],
  )

  const [rows, setRows] = useState<PosPasserbyBillsListRow[]>([])

  const { billsQueryAsRows, productFragmentAsRow, columns } = usePosPasserbyBillsList(
    (rowId, canRemove: boolean) => {
      if (canRemove) {
        setRows([...rows.filter((row) => row.id !== rowId)])
      } else {
        showError(t('pms.booking.pos.cannot_delete_split_bill_item'))
      }
    },
    (updatedRow) => {
      setRows(rows.map((row) => (row.id === updatedRow.id ? updatedRow : row)))
    },
  )

  useEffect(() => {
    if (billsData && rows.length === 0) setRows(billsQueryAsRows(billsData))
  }, [billsData])

  //#endregion

  //
  //
  //#region products
  const { data: productsResult, loading: loadingProducts } = useProductsQuery({
    variables: {
      filter: {
        tenantId: { eq: tenantId },
        propertyId: { eq: propertyId },
        chargeCategoryId: selectedChargeCategory ? { eq: selectedChargeCategory.id } : undefined,
      },
    },
  })
  //#endregion

  //
  //
  //#region deposit
  const {
    data: depositsPosResult,
    loading: loadingPosDeposits,
    refetch: refetchPosDeposits,
  } = usePosDepositDocumentsQuery({
    notifyOnNetworkStatusChange: true,
    skip: true,
  })

  const fetchDeposits = useCallback(
    async (billId: string) => {
      try {
        const result = await refetchPosDeposits({
          filter: {
            tenantId: { eq: tenantId },
            billId,
          },
        })

        if (!result.errors && result.data) {
          return result.data.depositDocuments
        }
      } catch (error) {
        return undefined
      }
    },
    [refetchPosDeposits],
  )

  //#endregion

  useEffect(() => {
    setLoading(loadingProducts || loadingBills || loadingCRUD)
  }, [loadingProducts, loadingBills, loadingCRUD])

  const onSaveCompleted = (updatedBill: BillListItemFragment) => {
    setBill(updatedBill)
    setRows(billsQueryAsRows(updatedBill))
    if (props.onSave) props.onSave(true)
  }

  const [openDocumentDrawer, setOpenDocumentDrawer] = useState(false)

  const onFinishAndSaveCompleted = (updatedBill: BillListItemFragment) => {
    console.log('onFinishAndSaveCompleted')
    setBill(updatedBill)
    setRows(billsQueryAsRows(updatedBill))
    setOpenDocumentDrawer(true)
  }

  //#region account balance drawer
  const [openAccountBalance, setOpenAccountBalance] = useState(false)
  const closeAccountBalance = () => setOpenAccountBalance(false)
  //#endregion

  //#region passerby bill drawer
  const [openPasserbyBill, setPasserbyBill] = useState(false)
  const closePasserbyBill = () => setPasserbyBill(false)
  //#endregion

  const [newProduct, setNewProduct] = useState<PosPasserbyBillsListRow | undefined>()

  useEffect(() => {
    if (newProduct) {
      setRows([...rows, newProduct])
      setNewProduct(undefined)
    }
  }, [rows, setRows, newProduct, setNewProduct])

  const checkin = bill && 'reservedResource' in bill ? getCheckinFromBill(bill) : null
  const checkout = bill && 'reservedResource' in bill ? getCheckoutFromBill(bill) : null

  const onClose = useCallback(() => {
    onCancel?.()
  }, [onCancel])

  const totalAmount = useMemo(() => {
    return rows.reduce((acc, curr) => {
      return acc + curr.amount * curr.quantity
    }, 0)
  }, [rows])

  const totalDepositAmount = useMemo(() => {
    return (
      posDeposits.reduce((acc, curr) => {
        return (
          acc +
          (curr.depositAssigneds?.reduce((acc2, curr2) => {
            return acc2 + curr2.amount
          }, 0) || 0)
        )
      }, 0) ?? 0
    )
  }, [posDeposits])

  const depositAssignedsIds = useMemo(() => {
    return (
      posDeposits.reduce((acc: string[], curr) => {
        const result = curr.depositAssigneds?.map((n) => n.id) ?? []
        return [...acc, ...result]
      }, []) ?? []
    )
  }, [posDeposits])

  const mainFetchBill = async (id: string) => {
    const billData = await fetchBill(id)
    const deposits = await fetchDeposits(id)
    if (billData) {
      setBill(billData)
      setRows(billsQueryAsRows(billData))
      setPosDeposits(deposits || [])
    }
  }

  useEffect(() => {
    const fn = async (id: string) => {
      await mainFetchBill(id)
    }
    if (!bill && billId) {
      fn(billId)
      return () => {
        setBill(null)
        setRows([])
        setPosDeposits([])
      }
    }
  }, [billId])

  return (
    <FormLayout //
      title={t('pms.booking.pos.title')}
      appearance={
        asPage
          ? { type: 'PAGE', id: HtmlElementId.TSH_PAGE_POS, fullWidth: true, removeMargin: true, removePadding: true }
          : { type: 'DRAWER', id: HtmlElementId.TSH_PAGE_POS, open: Boolean(billId), onClose: onClose, width: '100%' }
      }
      toolbar={
        <Stack spacing={2} direction="row" sx={{ width: '100%', paddingTop: '8px', paddingBottom: '8px' }} justifyContent="flex-end" alignItems="center">
          {props.onCancel && (
            <Button disabled={loading} variant="outlined" sx={{ height: '40px' }} onClick={() => props.onCancel?.()}>
              <Stack direction="row" spacing={1} alignItems="center">
                {t('commons.cancel')}
              </Stack>
            </Button>
          )}

          <Button
            disabled={loading}
            variant="contained"
            sx={{ height: '40px' }}
            onClick={() => {
              if (isFragment(bill)) {
                if (rows.length === 0) {
                  confirm({
                    title: t('pms.booking.pos.save.select_some_products.title'),
                    message: t('pms.booking.pos.save.select_some_products.message'),
                    confirmButtonText: t('commons.confirm'),
                    cancelButtonText: t('commons.cancel'),
                    onConfirm: () => {
                      editBill(bill, rows, onSaveCompleted)
                    },
                  })
                } else {
                  editBill(bill, rows, onSaveCompleted)
                }
              } else {
                if (rows.length === 0) {
                  if (billRelatedToReservation) {
                    confirm({
                      title: t('pms.booking.pos.save.select_some_products.title'),
                      message: t('pms.booking.pos.save.dummy_select_some_products.message'),
                      confirmButtonText: t('commons.confirm'),
                      cancelButtonText: t('commons.cancel'),
                      onConfirm: () => {
                        props.onCancel?.()
                      },
                    })
                  } else {
                    infoSnackBar(t('pms.booking.pos.save.dummy_select_some_products.message_info'))
                  }
                } else {
                  confirm({
                    title: t('pms.booking.pos.save.select_some_products.title'),
                    message: t('pms.booking.pos.save.dummy_create_bill.message'),
                    confirmButtonText: t('commons.confirm'),
                    cancelButtonText: t('commons.cancel'),
                    onConfirm: async () => {
                      const dummyBill = await createDummyBill()
                      if (dummyBill) {
                        editBill(dummyBill, rows, onSaveCompleted)
                      }
                    },
                  })
                }
              }
            }}
          >
            {t('commons.save')}
          </Button>
        </Stack>
      }
    >
      <Box
        sx={{
          display: 'flex',
          height: { xs: 'calc(100vh - 200px)', sm: 'calc(100vh - 220px)', md: 'calc(100vh - 240px)' },
          flexDirection: 'column',
          width: { xs: 'calc(100vw - 16px)', md: '100%' },
          boxSizing: 'border-box',
        }}
      >
        <Box sx={{ flex: '0 0 auto', pb: 1 }}>
          <PosActionsRow
            isPasserbyBill={isFragment(bill) ? (bill.reservation ?? []).length <= 0 : true}
            loading={loading}
            propertyId={propertyId}
            bill={bill}
            setBill={async (newBill) => {
              const unsavedChanges = rows.findIndex((row) => row.productionId.length === 0) >= 0
              if (bill?.id !== newBill?.id && unsavedChanges) {
                confirm({
                  title: t('pms.booking.passerby-bill.change-bill-dialog.title'),
                  message: t('pms.booking.passerby-bill.change-bill-dialog.message'),
                  confirmButtonText: t('pms.booking.passerby-bill.change-bill-dialog.confirmButton'),
                  cancelButtonText: t('commons.cancel'),
                  onConfirm: async () => {
                    const deposits = newBill?.id ? await fetchDeposits(newBill.id) : []
                    if (deposits) setPosDeposits(deposits || [])
                    setRows([...billsQueryAsRows(newBill), ...rows.filter((r) => r.productId !== undefined)])
                    setBill(newBill)
                  },
                })
              } else if ((isFragment(newBill) && isId(bill)) || !unsavedChanges) {
                const deposits = newBill?.id ? await fetchDeposits(newBill.id) : []
                if (deposits) setPosDeposits(deposits || [])
                setRows([...billsQueryAsRows(newBill)])
                setBill(newBill)
              }
            }}
            finishAndPay={async () => {
              if (rows.length === 0) {
                infoSnackBar(t('pms.booking.pos.finish_pay.select_some_products'))
              } else if (bill) {
                isFragment(bill) ? editBill(bill, rows, onFinishAndSaveCompleted) : showError(t('pms.booking.pos.missing_bill_for_edit'))
              } else {
                const dummyBill = await createDummyBill()
                if (dummyBill) {
                  editBill(dummyBill, rows, onFinishAndSaveCompleted)
                }
              }
            }}
            accountBalance={async () => {
              if (bill?.id) {
                setOpenAccountBalance(true)
              } else {
                showError(t('pms.booking.pos.missing_bill_for_account_balance_generation'))
              }
            }}
            onPasserbyBill={() => {
              setPasserbyBill(true)
            }}
          />
        </Box>

        <Box sx={{ flex: 1, overflowY: 'auto', boxSizing: 'border-box' }}>
          <Grid container spacing={2} sx={{ height: '100%' }}>
            <Grid item xs={12} md={4}>
              <Box sx={{ height: { xs: 'calc(50vh - 130px)', md: 'calc(100vh - 290px)' }, width: { xs: 'auto', sm: '100%' }, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ flex: '0 0 auto' }}>
                  <Typography variant={'titleSmall'}>{t('pms.booking.pos.charges_list')}</Typography>
                  <PosChargeCategorySelector loading={loading} propertyId={propertyId} onChargeCategorySelected={setSelectedChargeCategory} />
                </Box>
                <Box sx={{ flex: 1, overflowY: 'auto', backgroundColor: 'var(--WhiteSmoke)', p: 0.5 }}>
                  <PosProducts
                    loading={loading}
                    productsResult={productsResult}
                    onProductSelected={(product) => {
                      const rowToUpdateIndex = rows.findIndex((row) => row.productId && row.productId === product.id && dateEquality(row.competenceDate, hotelDate))
                      const updatedRows = [...rows]
                      if (rowToUpdateIndex >= 0) {
                        const newQuantity = updatedRows[rowToUpdateIndex].quantity + 1
                        updatedRows[rowToUpdateIndex] = {
                          ...updatedRows[rowToUpdateIndex],
                          quantity: newQuantity,
                          total: newQuantity * updatedRows[rowToUpdateIndex].amount,
                        }
                      } else {
                        updatedRows.unshift(productFragmentAsRow(product, bill))
                      }
                      setRows(updatedRows)
                    }}
                  />
                </Box>
                <Box sx={{ flex: '0 0 auto' }}>
                  <PosNewProduct
                    propertyId={propertyId}
                    loading={loading}
                    onProductCreatedWithSuccess={(insertedProduct) => {
                      setNewProduct({
                        id: uuid(),
                        competenceDate: insertedProduct.competenceDate,
                        quantity: 1,
                        productionId: '',
                        productId: '',
                        detail: insertedProduct.description,
                        currency: currency,
                        amount: insertedProduct.amount,
                        total: insertedProduct.amount,
                        chargeCategoryId: insertedProduct.chargeCategoryId,
                        category: insertedProduct.chargeCategory.name,
                        vat: insertedProduct.chargeCategory.defaultVat.code,
                        vatId: insertedProduct.chargeCategory.defaultVatId,
                        editable: true,
                        checkin,
                        checkout,
                      })
                    }}
                    currentSelectedChargeCategory={selectedChargeCategory}
                    checkin={checkin}
                    checkout={checkout}
                  />
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={8} sx={{ display: 'flex', flexDirection: 'column', height: { xs: '100%', sm: 'calc(50vh - 140px)', md: 'calc(100vh - 290px)' } }}>
              <Stack direction="row" spacing={1}>
                <Typography variant={'titleSmall'}>{t('pms.booking.pos.charges_in_bill')}</Typography>
                <Typography variant={'titleSmall'}>
                  <b>{formatCurrency(totalAmount, currency)}</b>
                </Typography>
                {totalDepositAmount > 0 && <PosDepositInfo totalDepositAmount={totalDepositAmount} posDepositDocumentIds={[...posDeposits.map((i) => i.id)]} />}
              </Stack>
              <Box sx={{ flexGrow: 2, overflowY: 'auto' }}>
                <HicDataGridPremium
                  {...hicCommonDataGridPremiumProps}
                  loading={loading}
                  isRowSelectable={() => false}
                  hideFooter={true}
                  columns={columns}
                  rows={[...rows].sort((r1, r2) => compareDates(r2.competenceDate, r1.competenceDate))}
                  disableColumnMenu
                  initialState={{
                    aggregation: {
                      model: {
                        total: 'sum',
                      },
                    },
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>

      {bill && (
        <CheckoutDocumentForm //
          propertyId={propertyId}
          open={openDocumentDrawer}
          onClose={() => setOpenDocumentDrawer(false)}
          billIds={[bill.id]}
          productionAssignedIds={[]}
          depositDocumentIds={[...posDeposits.map(({ id }) => id)]}
          onSuccess={({ documentId }) => {
            setBill(null)
            setRows([])
            setPrintDocumentId(documentId)
          }}
          onCreditNoteSuccess={() => setOpenDocumentDrawer(false)}
        />
      )}
      {printDocumentId && (
        <DocumentReportViewer
          open={printDocumentId !== null}
          setClose={() => {
            setPrintDocumentId(null)
          }}
          documentId={printDocumentId}
          propertyId={propertyId}
        />
      )}
      {openAccountBalance ? (
        <AccountBalanceReportViewer //
          bills={bill?.id ? [{ billId: bill.id }] : undefined}
          open={openAccountBalance}
          setClose={closeAccountBalance}
          propertyId={propertyId}
          selectedDepositAssignedIds={[...depositAssignedsIds]}
        />
      ) : undefined}
      {openPasserbyBill && (
        <PasserbyBillListDrawer
          open={openPasserbyBill}
          onClose={closePasserbyBill}
          propertyId={propertyId}
          onPasserbyBillItemClick={async (passerbyBillId) => {
            await mainFetchBill(passerbyBillId)
            closePasserbyBill()
          }}
        />
      )}
    </FormLayout>
  )
}
